import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Button, ButtonGroup, Input, ModalWrapper, Prompt } from "@/shared/ui";
import { extractErrorMessage } from "@/shared/utils";
import { useCreateChatChannelMutation } from "../api";
import { chatChannelSchema } from "../validation";
import type { ChatChannelFormValues } from "../validation";
import ChatChannelMembersTab from "./ChatChannelMembersTab";

const ISSUE_TO_I18N: Record<string, string> = {
  "Channel with this name already exists in your organization": "channelForm.name.error.taken",
  "Only A-Z, a-z, 0-9, _ and - are allowed": "channelForm.name.error.invalid",
};

interface PromptState {
  title: string;
  text: string;
  variant: "success" | "error";
}

interface Props {
  onClose: () => void;
}

export default function ChatChannelCreateModal({ onClose }: Props) {
  const { t } = useTranslation("ChatPage");
  const [createChannel, { isLoading }] = useCreateChatChannelMutation();
  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const [activeTab, setActiveTab] = useState<"main" | "members">("main");

  const {
    register,
    handleSubmit,
    setError,
    trigger,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<ChatChannelFormValues>({
    mode: "onChange",
    resolver: zodResolver(chatChannelSchema),
    defaultValues: { name: "", display_name: "", member_user_ids: [] },
  });

  const memberUserIds = watch("member_user_ids");

  function handleTabChange(val: string) {
    if (val === "members" && !isValid) {
      trigger(["name", "display_name"]);
      return;
    }
    setActiveTab(val as "main" | "members");
  }

  function handleMembersChange(ids: number[]) {
    setValue("member_user_ids", ids);
  }

  async function onSubmit(data: ChatChannelFormValues) {
    try {
      await createChannel({ name: data.name, display_name: data.display_name, member_user_ids: data.member_user_ids }).unwrap();
      onClose();
    } catch (err: unknown) {
      const details = (err as { data?: { details?: Array<{ field?: string; issue?: string }> } })?.data?.details;
      if (Array.isArray(details) && details.length > 0) {
        let hasFieldErrors = false;
        let hasMemberError = false;
        for (const d of details) {
          if (d?.field === "name" && d?.issue) {
            setError("name", { message: ISSUE_TO_I18N[d.issue] ?? d.issue });
            hasFieldErrors = true;
          } else if (d?.field === "display_name" && d?.issue) {
            setError("display_name", { message: ISSUE_TO_I18N[d.issue] ?? d.issue });
            hasFieldErrors = true;
          } else if (d?.field === "member_user_ids" && d?.issue) {
            hasMemberError = true;
          }
        }
        if (hasFieldErrors) {
          setActiveTab("main");
        } else if (hasMemberError) {
          setActiveTab("members");
          setPrompt({ title: t("manage.errorTitle"), text: t("channelForm.members.error.invalidUser"), variant: "error" });
        }
        return;
      }
      setPrompt({ title: t("manage.errorTitle"), text: extractErrorMessage(err, "manage.defaultError", t), variant: "error" });
    }
  }

  const tabOptions = [
    { label: t("channelForm.tabs.main"), value: "main" },
    { label: t("channelForm.tabs.members"), value: "members", disabled: !isValid },
  ];

  return (
    <ModalWrapper onClose={onClose} width="w-xl">
      <form className="flex flex-col gap-6">
        <h2 className="text-display-2xs content-base-primary pr-8">{t("manage.createTitle")}</h2>

        <ButtonGroup options={tabOptions} value={activeTab} onChange={handleTabChange} fullWidth />

        {activeTab === "main" ? (
          <div className="flex flex-col gap-3">
            <Input
              label={t("manage.fields.name.label")}
              placeholder={t("manage.fields.name.placeholder")}
              error={errors.name?.message ? t(errors.name.message) : undefined}
              {...register("name")}
            />
            <p className="text-body-regular-xs content-base-secondary -mt-1">{t("manage.fields.name.hint")}</p>
            <Input
              label={t("manage.fields.displayName.label")}
              placeholder={t("manage.fields.displayName.placeholder")}
              error={errors.display_name?.message ? t(errors.display_name.message) : undefined}
              {...register("display_name")}
            />
          </div>
        ) : (
          <ChatChannelMembersTab value={memberUserIds} onChange={handleMembersChange} />
        )}

        <div className="grid grid-cols-2 gap-3">
          {activeTab === "main" ? (
            <>
              <Button variant="secondary" size="md" type="button" onClick={onClose}>
                {t("manage.cancel")}
              </Button>
              <Button
                variant="primary"
                size="md"
                type="button"
                disabled={!isValid}
                onClick={() => setActiveTab("members")}>
                {t("channelForm.next")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" size="md" type="button" onClick={() => setActiveTab("main")}>
                {t("channelForm.back")}
              </Button>
              <Button variant="primary" size="md" type="button" disabled={isLoading} onClick={handleSubmit(onSubmit)}>
                {t("manage.submit.create")}
              </Button>
            </>
          )}
        </div>
      </form>

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => setPrompt(null)}
          namespace="ChatPage"
        />
      )}
    </ModalWrapper>
  );
}
